import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Typography } from "@mui/material";
import * as React from "react";
import { ReactElement, useContext, useState } from "react";
import FroalaEditorView from "react-froala-wysiwyg/FroalaEditorView";

import { ProgressModal } from "../../../components";
import HorizontalBox from "../../../components/Boxes/HorizontalBox/HorizontalBox";
import Button from "../../../components/Buttons/Button/Button";
import { AppContext } from "../../../core/context/appContextProvider";
import {
  getAvailableDomains,
  getClientConfigsList,
} from "../../../services/client.service";
import { getAvailableRoles } from "../../../services/contact.service";
import {
  getContactsPreviews,
  getDocumentsPreviews,
  getEmailImage,
  getGeneralMailing,
} from "../../../services/generalMailings.service";
import { getClientConfigs } from "../../../services/preAuthAPIs.service";
import { useEffectAsync } from "../../../utils/hooks/useEffectAsync.hook";
import {
  GeneralMailings,
  SelectedContacts,
  SelectedDocuments,
} from "../../../utils/types/generalMailings.type";
import {
  BodyBox,
  HeaderRow,
  LinkButton,
  StyledBox,
  StyledModal,
  Title,
} from "./MailingsPreviewPanel.styles";

type Props = {
  showModal: boolean;
  setShowModal: any;
  selectedPreviewMailing: any;
  setSelectedPreviewMailing: any;
  previewCounter: number;
  setPreviewCounter: any;
};

const MailingsPreviewPanel: React.FC<Props> = (props: Props): ReactElement => {
  const {
    showModal,
    setShowModal,
    selectedPreviewMailing,
    setSelectedPreviewMailing,
    previewCounter,
    setPreviewCounter,
  } = props;

  const [mailingData, setMailingData] = useState<GeneralMailings>();
  const [docTypeOpen, setDocTypeOpen] = useState<boolean>(false);
  const [contactTypeOpen, setContactTypeOpen] = useState<boolean>(false);
  const [multiplePreviewsOpen, setMultiplePreviewsOpen] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const { informationAlert, state } = useContext(AppContext);
  const [ccEmails, setCcEmails] = useState("");
  const [bcEmails, setBcEmails] = useState("");
  const [subject, setSubject] = useState("");
  const [documentNames, setDocumentNames] = useState<Array<string>>([]);
  const [displayDocs, setDisplayDocs] = useState<string>("");
  const [groupedEmails, setGroupedEmails] = useState<string[]>([]);
  const [displayEmails, setDisplayEmails] = useState<string>("");
  const [messageBodies, setMessageBodies] = useState<Array<string>>([]);
  const [froalaModal, setFroalaModal] = useState("");
  const [fundNames, setFundNames] = useState<Array<string>>([]);
  const [investorNames, setInvestorNames] = useState<Array<string>>([]);
  const [docFundNames, setDocFundNames] = useState<Array<string>>([]);
  const [docInvestorNames, setDocInvestorNames] = useState<Array<string>>([]);
  const [displayFunds, setDisplayFunds] = useState<string>("");
  const [displayInvestors, setDisplayInvestors] = useState<string>("");
  const [displayDocFund, setDisplayDocFund] = useState<string>("");
  const [displayDocInvestor, setDisplayDocInvestor] = useState<string>("");
  const [displayDocRoles, setDisplayDocRoles] = useState<string>("");
  const [displaySubject, setDisplaySubject] = useState<string>("");
  const [subjectFields, setSubjectFields] = useState<Array<string>>([]);
  const [contactPreviewLength, setContactPreviewLength] = useState(0);
  const [documentPreviewLength, setDocumentPreviewLength] = useState(0);
  const [previewCounterContacts, setPreviewCounterContacts] = useState(0);
  const [previewCounterDocs, setPreviewCounterDocs] = useState(0);
  const [contactRoles, setContactRoles] = useState("");

  const close = () => {
    setSelectedPreviewMailing("");
    setDocumentNames([]);
    setCcEmails("");
    setBcEmails("");
    setSubject("");
    setDocTypeOpen(false);
    setContactTypeOpen(false);
    setMultiplePreviewsOpen(false);
    setShowModal(false);
    setDisplayFunds("");
    setDisplayInvestors("");
    setDisplayDocs("");
    setDisplayEmails("");
    setDocumentNames([]);
    setGroupedEmails([]);
    setFroalaModal("");
    setFundNames([]);
    setInvestorNames([]);
    setContactPreviewLength(0);
    setDocumentPreviewLength(0);
    setPreviewCounterContacts(0);
    setPreviewCounterDocs(0);
    setPreviewCounter(0);
    setMessageBodies([]);
    setDisplayDocFund("");
    setDisplayDocInvestor("");
  };

  const nextPreview = () => {
    if (contactTypeOpen) {
      setPreviewCounterContacts(previewCounterContacts + 1);
      setDisplayFunds(fundNames[previewCounterContacts + 1]);
      setDisplayInvestors(investorNames[previewCounterContacts + 1]);
      setDisplayEmails(groupedEmails[previewCounterContacts + 1]);
      setFroalaModal(messageBodies[previewCounterContacts + 1]);
      setDisplaySubject(subjectFields[previewCounterContacts + 1]);
    }
    if (docTypeOpen) {
      setPreviewCounterDocs(previewCounterDocs + 1);
      setDisplayDocs(documentNames[previewCounterDocs + 1]);
      setDisplayEmails(groupedEmails[previewCounterDocs + 1]);
      setFroalaModal(messageBodies[previewCounterDocs + 1]);
      setDisplayDocFund(docFundNames[previewCounterDocs + 1]);
      setDisplayDocInvestor(docInvestorNames[previewCounterDocs + 1]);
      setDisplaySubject(subjectFields[previewCounterDocs + 1]);
    }
  };

  const prevPreview = () => {
    if (contactTypeOpen) {
      setPreviewCounterContacts(previewCounterContacts - 1);
      setDisplayFunds(fundNames[previewCounterContacts - 1]);
      setDisplayInvestors(investorNames[previewCounterContacts - 1]);
      setDisplayEmails(groupedEmails[previewCounterContacts - 1]);
      setFroalaModal(messageBodies[previewCounterContacts - 1]);
      setDisplaySubject(subjectFields[previewCounterContacts - 1]);
    }
    if (docTypeOpen) {
      setPreviewCounterDocs(previewCounterDocs - 1);
      setDisplayDocs(documentNames[previewCounterDocs - 1]);
      setDisplayEmails(groupedEmails[previewCounterDocs - 1]);
      setFroalaModal(messageBodies[previewCounterDocs - 1]);
      setDisplayDocFund(docFundNames[previewCounterDocs - 1]);
      setDisplayDocInvestor(docInvestorNames[previewCounterDocs - 1]);
      setDisplaySubject(subjectFields[previewCounterDocs - 1]);
    }
  };

  useEffectAsync(async () => {
    setIsLoading(true);

    if (selectedPreviewMailing) {
      try {
        const mailing: any = await getGeneralMailing(selectedPreviewMailing);

        const imageIds: any = mailing.imageIds;
        let body: any = mailing.messageBody;
        const imageDataList: any = [];

        if (imageIds?.length) {
          await Promise.all(
            imageIds.map(async (id: any): Promise<any> => {
              const imageData = await getEmailImage(id);

              imageDataList.push(imageData);
            })
          );
        }

        const el = document.createElement("div");

        if (imageIds?.length) {
          el.innerHTML = body;

          el.querySelectorAll("img").forEach((imgEl: any) => {
            imageDataList.map((imageData: any) => {
              if (imgEl.id === imageData.id) {
                imgEl.src = `data:image/png;base64,${imageData.content}`;
              }
            });
          });

          body = el.innerHTML;
        } else {
          body = body;
        }

        if (mailing.type === "DOCUMENTS") {
          setDocTypeOpen(true);
          const payload: SelectedDocuments = {
            fileIds: mailing.documents,
          };
          const documents = await getDocumentsPreviews(payload);

          setDocumentPreviewLength(documents.previews.length);
          const roles: any = [];
          const fileNames: any = documents.previews.map(
            (doc: any) => doc.fileName
          );
          const fundNames: any = documents.previews.map(
            (preview: any) => preview.fundName
          );
          const investorNames: any = documents.previews.map(
            (preview: any) => preview.investorName
          );
          const emailGroups: Array<any> = documents.previews.map(
            (doc: any) => doc.emails
          );
          const roleGroups: any = documents.previews.map((doc: any) =>
            doc.folderPermissionNames.forEach((name: any) => roles.push(name))
          );
          const emailStrings = emailGroups.map((group) => group.join(", "));
          const sentEmailList = emailGroups;
          
          const fileDownloadLinks: any = documents.fileDownloadLinks;
          const subject: string = mailing.subject;

          const displayRoles: any = Array.from(new Set(roles));

          let bodyCounter = 0;
          const linkedBodies = documents.previews.map((preview: any) => {
            const fileId = preview.fileId;
            const docLink = fileDownloadLinks.filter(
              (link: any) => link.fileId === fileId
            );

            const docField = body.replaceAll(
              "&lt;document_link&gt;",
              `<a href="${docLink[0].downloadLink}" target="_blank" rel="noopener noreferrer" fr-original-style="" style="user-select: auto; box-sizing: inherit;">${fileNames[bodyCounter]}</a>`
            );
            const nameField = docField.replaceAll(
              "&lt;investor_name&gt;",
              `${preview.investorName}`
            );
            const allLinked = nameField.replaceAll(
              "&lt;fund_name&gt;",
              `${preview.fundName}`
            );

            bodyCounter++;
            return allLinked;
          });

          let subjectCounter = 0;
          const linkedSubjects = documents.previews.map((preview: any) => {
            const subjectField = subject.replaceAll(
              "<investor_name>",
              `${preview.investorName}`
            );
            const allLinked = subjectField.replaceAll(
              "<fund_name>",
              `${preview.fundName}`
            );

            subjectCounter++;
            return allLinked;
          });

          setMessageBodies(linkedBodies);
          setSubjectFields(linkedSubjects);
          setDocumentNames(fileNames);
          setGroupedEmails(emailStrings);
          setDocFundNames(fundNames);
          setDocInvestorNames(investorNames);
          setDisplayDocRoles(displayRoles.join(", "));
          if (documents.previews.length > 1) {
            setMultiplePreviewsOpen(true);
          }
          if (!multiplePreviewsOpen) {
            setDisplayDocs(fileNames[0]);
            setDisplayDocFund(docFundNames[0]);
            setDisplayDocInvestor(docInvestorNames[0]);
            setDisplayEmails(emailStrings[0]);
            setFroalaModal(linkedBodies[0]);
            setDisplaySubject(linkedSubjects[0]);
          }
          if (previewCounter !== 0 && documents.previews.length > 1) {
            setDisplayDocs(fileNames[previewCounter - 1]);
            setDisplayDocFund(fundNames[previewCounter - 1]);
            setDisplayDocInvestor(investorNames[previewCounter - 1]);
            setDisplayEmails(emailStrings[previewCounter - 1]);
            setFroalaModal(linkedBodies[previewCounter - 1]);
            setPreviewCounterDocs(previewCounter - 1);
            setDisplaySubject(linkedSubjects[previewCounter - 1]);
          }
        }

        if (mailing.type === "CONTACTS" || mailing.type === "CAP_CALL") {
          setContactTypeOpen(true);
          const payload: SelectedContacts = {
            funds: mailing.to.funds,
            investors: mailing.to.investors,
            roles: mailing.to.roles,
          };
          const contacts = await getContactsPreviews(payload);

          setContactPreviewLength(contacts.previews.length);
          const fundNames: any = contacts.previews.map(
            (contact: any) => contact.fundName
          );
          const investorNames: any = contacts.previews.map(
            (contact: any) => contact.investorName
          );

          const emailGroups: Array<any> = contacts.previews.map(
            (contact: any) => contact.emails
          );

          const emailStrings = emailGroups.map((group) => group.join(", "));

          const sentEmailList = emailStrings;

          const subject: string = mailing.subject;
          let bodyCounter = 0;
          const linkedBodies = contacts.previews.map((preview: any) => {
            const nameField = body.replaceAll(
              "&lt;investor_name&gt;",
              `${preview.investorName}`
            );
            const allLinked = nameField.replaceAll(
              "&lt;fund_name&gt;",
              `${preview.fundName}`
            );

            bodyCounter++;
            return allLinked;
          });
          let subjectCounter = 0;
          const linkedSubjects = contacts.previews.map((preview: any) => {
            const subjectField = subject.replaceAll(
              "<investor_name>",
              `${preview.investorName}`
            );
            const allLinked = subjectField.replaceAll(
              "<fund_name>",
              `${preview.fundName}`
            );

            subjectCounter++;
            return allLinked;
          });

          const availableRoles = await getAvailableRoles();

          const rolesList: any = [];

          mailing.to.roles.forEach((id: any) => {
            const role = availableRoles.filter((role: any) => role.id === id);
            const name = role[0].name;

            rolesList.push(name);
          });

          setContactRoles(rolesList.join(", "));
          setMessageBodies(linkedBodies);
          setSubjectFields(linkedSubjects);
          setFundNames(fundNames);
          setInvestorNames(investorNames);
          setGroupedEmails(emailStrings);
          if (contacts.previews.length > 1) {
            setMultiplePreviewsOpen(true);
          }
          if (!multiplePreviewsOpen) {
            setDisplayFunds(fundNames[0]);
            setDisplayInvestors(investorNames[0]);
            setDisplayEmails(emailStrings[0]);
            setFroalaModal(linkedBodies[0]);
            setDisplaySubject(linkedSubjects[0]);
          }
          if (previewCounter !== 0 && contacts.previews.length > 1) {
            setDisplayFunds(fundNames[previewCounter - 1]);
            setDisplayInvestors(investorNames[previewCounter - 1]);
            setDisplayEmails(emailStrings[previewCounter - 1]);
            setPreviewCounterContacts(previewCounter - 1);
            setFroalaModal(linkedBodies[previewCounter - 1]);
            setDisplaySubject(linkedSubjects[previewCounter - 1]);
          }
        }

        setMailingData(mailing);
        setCcEmails(mailing.cc.toString());
        setBcEmails(mailing.bcc.toString());
        //setSubject(mailing.subject);
      } catch (error) {
        informationAlert("Error occured when loading mailing preview", "error");
      }
    }
    setIsLoading(false);
  }, [selectedPreviewMailing]);

  return (
    <React.Fragment key="center_preview_panel">
      <ProgressModal id="modal_previews_loading" showProgress={isLoading} />
      <StyledModal
        id="mailings_preview_modal"
        open={showModal}
        onClose={() => {
          close();
        }}
        sx={{ borderRadius: "10px" }}
      >
        <StyledBox sx={{ borderRadius: "10px" }}>
          <HorizontalBox
            sx={{
              justifyContent: "center",
              backgroundColor: "#1f2e96",
              width: "100%",
              height: "80px",
              paddingBottom: "30px",
              color: "primary",
              borderTopLeftRadius: "10px",
              borderTopRightRadius: "10px",
            }}
          >
            <HeaderRow id="title">
              <Typography
                variant="formTitle"
                sx={{ color: "#FFFFFF", paddingTop: "45px" }}
              >
                Preview{" "}
                {multiplePreviewsOpen
                  ? docTypeOpen
                    ? `(${previewCounterDocs + 1} of ${documentPreviewLength})`
                    : `(${
                        previewCounterContacts + 1
                      } of ${contactPreviewLength})`
                  : ""}
              </Typography>
            </HeaderRow>
          </HorizontalBox>
          <BodyBox
            sx={{ flex: "wrap", width: "100%", backgroundColor: "#FFFFFF" }}
          >
            {contactTypeOpen ? (
              <>
                <Typography fontWeight={"bold"} fontSize={"small"} sx={{}}>
                  TO: {displayFunds} - {displayInvestors}
                </Typography>
                <Typography fontSize={"small"} sx={{ paddingBottom: "15px" }}>
                  {displayEmails}
                </Typography>
                <Typography fontWeight={"bold"} fontSize={"small"} sx={{}}>
                  ROLES:
                </Typography>
                <Typography fontSize={"small"} sx={{ paddingBottom: "15px" }}>
                  {contactRoles}
                </Typography>
              </>
            ) : (
              <>
                <Typography fontWeight={"bold"} fontSize={"small"}>
                  ATTACHED:
                </Typography>
                <Typography fontSize={"small"} sx={{ paddingBottom: "15px" }}>
                  {displayDocs}
                </Typography>
                <Typography
                  fontWeight={"bold"}
                  fontSize={"small"}
                  sx={{ width: "100%" }}
                >
                  TO: {displayDocFund} - {displayDocInvestor}
                </Typography>
                <Typography fontSize={"small"} sx={{ paddingBottom: "15px" }}>
                  {displayEmails}
                </Typography>
                <Typography fontWeight={"bold"} fontSize={"small"} sx={{}}>
                  ROLES:
                </Typography>
                <Typography fontSize={"small"} sx={{ paddingBottom: "15px" }}>
                  {displayDocRoles}
                </Typography>
              </>
            )}
            {ccEmails ? (
              <>
                <Typography fontWeight={"bold"} fontSize={"small"} sx={{}}>
                  CC:
                </Typography>
                <Typography fontSize={"small"} sx={{ paddingBottom: "15px" }}>
                  {ccEmails}
                </Typography>
              </>
            ) : (
              ""
            )}
            {bcEmails ? (
              <>
                <Typography fontWeight={"bold"} fontSize={"small"} sx={{}}>
                  BCC:
                </Typography>
                <Typography fontSize={"small"} sx={{ paddingBottom: "15px" }}>
                  {bcEmails}
                </Typography>
              </>
            ) : (
              ""
            )}
            <Typography fontWeight={"bold"} fontSize={"small"}>
              SUBJECT:
            </Typography>
            <Typography fontSize={"small"} sx={{}}>
              {displaySubject}
            </Typography>
            <FroalaEditorView model={froalaModal} />
          </BodyBox>
          <HorizontalBox
            attachToBottom={true}
            fullWidth={true}
            sx={{
              justifyContent: "space-evenly",
              alingItems: "center",
              backgroundColor: "#FFFFFF",
              paddingTop: "5px",
              borderBottomLeftRadius: "10px",
              borderBottomRightRadius: "10px",
            }}
          >
            <LinkButton
              id="back_preview"
              variant="text"
              color="primary"
              name="Previous"
              disableElevation
              disabled={
                (multiplePreviewsOpen && previewCounterContacts >= 1) ||
                previewCounterDocs >= 1
                  ? false
                  : true
              }
              onClick={() => {
                prevPreview();
              }}
              startIcon={<ArrowBackIosRoundedIcon />}
              sx={{ right: "12px" }}
            >
              Previous
            </LinkButton>
            <LinkButton
              id="close_preview"
              variant="text"
              color="primary"
              name="Close"
              disableElevation
              onClick={() => {
                close();
              }}
              sx={{ right: "12px" }}
            >
              Close
            </LinkButton>
            <LinkButton
              id="next_preview"
              variant="text"
              name="Next"
              color="primary"
              disableElevation
              disabled={
                (multiplePreviewsOpen &&
                  previewCounterContacts < contactPreviewLength - 1) ||
                previewCounterDocs < documentPreviewLength - 1
                  ? false
                  : true
              }
              onClick={() => {
                nextPreview();
              }}
              endIcon={<ArrowForwardIosIcon />}
              sx={{ right: "12px" }}
            >
              Next
            </LinkButton>
          </HorizontalBox>
        </StyledBox>
      </StyledModal>
    </React.Fragment>
  );
};

export default MailingsPreviewPanel;
